"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistContextManager = void 0;
const context_helper_1 = require("./context.helper");
const api_helper_1 = require("./api.helper");
class AIAssistContextManager {
    /**
     * Update context from raw messages with conversion and cleaning
     * @param messages Raw messages to convert
     * @param currentState Current context state
     * @param options Context processing options
     * @returns Updated context state
     */
    static updateContextFromMessages(messages, htmlStripService, currentState, options = {}) {
        // Convert messages to IFrontChatMessage format
        const convertedMessages = api_helper_1.AIAssistApiHelper.convertMessageToFrontChatMessage(messages);
        // Clean and validate messages
        const cleanedMessages = context_helper_1.AIAssistContextHelper.stripHtmlFromMessages(convertedMessages, htmlStripService);
        // Apply context options (limiting, filtering, etc.)
        const processedMessages = this.processContextMessages(cleanedMessages, options);
        return {
            ...currentState,
            currentContext: processedMessages,
            contextSummary: this.generateContextSummary(processedMessages, []),
            messageCount: processedMessages.length,
            hasHtml: this.checkForHtmlContent(convertedMessages)
        };
    }
    /**
     * Update context safely with HTML stripping
     * @param messages New context messages
     * @param currentState Current context state
     * @returns Updated context state
     */
    static updateContextSafe(messages, htmlStripService, currentState) {
        const cleanedMessages = context_helper_1.AIAssistContextHelper.stripHtmlFromMessages(messages, htmlStripService);
        return {
            ...currentState,
            currentContext: cleanedMessages,
            contextSummary: this.generateContextSummary(cleanedMessages, []),
            messageCount: cleanedMessages.length,
            hasHtml: this.checkForHtmlContent(messages)
        };
    }
    /**
     * Process context messages with options
     * @param messages Messages to process
     * @param options Processing options
     * @returns Processed messages
     */
    static processContextMessages(messages, options) {
        const { maxMessages = 50, maxLength = 8000, includeSystemMessages = false, includeAssistantMessages = true, includeUserMessages = true } = options;
        // Filter messages based on role
        let filteredMessages = messages.filter(msg => {
            if (msg.role === 'system' && !includeSystemMessages)
                return false;
            if (msg.role === 'assistant' && !includeAssistantMessages)
                return false;
            if (msg.role === 'user' && !includeUserMessages)
                return false;
            return true;
        });
        // Limit number of messages
        if (filteredMessages.length > maxMessages) {
            filteredMessages = filteredMessages.slice(-maxMessages);
        }
        // Limit total length
        if (maxLength > 0) {
            filteredMessages = this.limitMessagesByLength(filteredMessages, maxLength);
        }
        return filteredMessages;
    }
    /**
     * Limit messages by total content length
     * @param messages Messages to limit
     * @param maxLength Maximum total length
     * @returns Limited messages
     */
    static limitMessagesByLength(messages, maxLength) {
        let totalLength = 0;
        const result = [];
        // Process from most recent to oldest
        for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            const msgLength = this.getMessageLength(msg);
            if (totalLength + msgLength > maxLength && result.length > 0) {
                break;
            }
            result.unshift(msg);
            totalLength += msgLength;
        }
        return result;
    }
    /**
     * Get message content length
     * @param message Message to measure
     * @returns Content length
     */
    static getMessageLength(message) {
        if (typeof message.content === 'string') {
            return message.content.length;
        }
        else if (Array.isArray(message.content)) {
            return message.content.reduce((total, item) => {
                var _a;
                return total + (((_a = item.text) === null || _a === void 0 ? void 0 : _a.length) || 0);
            }, 0);
        }
        return 0;
    }
    /**
     * Check if messages contain HTML content
     * @param messages Messages to check
     * @returns True if HTML found
     */
    static checkForHtmlContent(messages) {
        return messages.some(msg => {
            if (typeof msg.content === 'string') {
                return /<[^>]*>/g.test(msg.content);
            }
            return false;
        });
    }
    /**
     * Generate context summary
     * @param messages Context messages
     * @param entries Assistant entries
     * @returns Context summary string
     */
    static generateContextSummary(messages, entries) {
        return context_helper_1.AIAssistContextHelper.generateContextSummary(messages, entries);
    }
    /**
     * Prepare context for API calls
     * @param messages Context messages
     * @param options Context options
     * @returns Prepared context data
     */
    static prepareApiContext(messages, htmlStripService, options = {}) {
        return context_helper_1.AIAssistContextHelper.prepareApiContext(messages, htmlStripService, {
            maxMessages: options.maxMessages || 20,
            maxLength: options.maxLength || 4000,
            includeSystemMessages: options.includeSystemMessages || false,
            includeAssistantMessages: options.includeAssistantMessages !== false,
            includeUserMessages: options.includeUserMessages !== false,
            stripHtml: options.stripHtml !== false
        });
    }
    /**
     * Extract relevant context for specific entry type
     * @param messages Context messages
     * @param entryType Type of assistant entry
     * @param maxLength Maximum context length
     * @returns Relevant context string
     */
    static extractRelevantContext(messages, entryType, htmlStripService, maxLength = 2000) {
        return context_helper_1.AIAssistContextHelper.extractRelevantContext(messages, entryType, htmlStripService, maxLength);
    }
    /**
     * Validate context state
     * @param state Context state to validate
     * @returns True if state is valid
     */
    static isValidContextState(state) {
        return state &&
            Array.isArray(state.currentContext) &&
            typeof state.isLLMStreaming === 'boolean';
    }
    /**
     * Create initial context state
     * @returns Initial context state
     */
    static createInitialState() {
        return {
            currentContext: [],
            isLLMStreaming: false,
            contextSummary: 'context.empty'.t(),
            messageCount: 0,
            hasHtml: false
        };
    }
    /**
     * Set LLM streaming state
     * @param currentState Current context state
     * @param streaming Whether LLM is streaming
     * @returns Updated context state
     */
    static setLLMStreaming(currentState, streaming) {
        return {
            ...currentState,
            isLLMStreaming: streaming
        };
    }
    /**
     * Get context statistics
     * @param state Context state
     * @returns Context statistics
     */
    static getContextStats(state) {
        const messages = state.currentContext;
        return {
            messageCount: messages.length,
            userMessages: messages.filter(m => m.role === 'user').length,
            assistantMessages: messages.filter(m => m.role === 'assistant').length,
            systemMessages: messages.filter(m => m.role === 'system').length,
            totalLength: messages.reduce((total, msg) => total + this.getMessageLength(msg), 0),
            hasHtml: state.hasHtml || false
        };
    }
    /**
     * Merge context from multiple sources
     * @param contexts Array of context states
     * @param options Merging options
     * @returns Merged context state
     */
    static mergeContexts(contexts, options = {}) {
        const allMessages = [];
        contexts.forEach(ctx => {
            allMessages.push(...ctx.currentContext);
        });
        // Remove duplicates and process
        const uniqueMessages = this.removeDuplicateMessages(allMessages);
        const processedMessages = this.processContextMessages(uniqueMessages, options);
        return {
            currentContext: processedMessages,
            isLLMStreaming: contexts.some(ctx => ctx.isLLMStreaming),
            contextSummary: this.generateContextSummary(processedMessages, []),
            messageCount: processedMessages.length,
            hasHtml: this.checkForHtmlContent(processedMessages)
        };
    }
    /**
     * Remove duplicate messages
     * @param messages Messages to deduplicate
     * @returns Unique messages
     */
    static removeDuplicateMessages(messages) {
        const seen = new Set();
        return messages.filter(msg => {
            const content = typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content);
            const key = `${msg.role}:${content}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    }
}
exports.AIAssistContextManager = AIAssistContextManager;
